// COP — the "view-as" DESCOPE SEAM (the ONE place apps/cop calls narrowHeld).
// CONTRACT:I4-POLICY-DECISION.1.0 — voluntary decision-time step-down over the vendored
//          @tdflite/classification-schema primitive (packages/tdflite-classification-schema,
//          TDFLite v0.10.0; drift-gated by scripts/check-classification-schema-drift.sh).
//
// NO-BYPASS: the view-as UI (ViewAsPanel) AND the visibility gate both import
// descopedClaimsFor / specDominatedByHeld from HERE — never narrowHeld directly — so the
// step-down semantics (identity, claims-key duality, no-normalization) live in exactly one
// place and cannot fork between the preview UI and the enforcement path.
//
// SEMANTICS (the contract, encoding the recon's risk mitigations):
//   • spec null / {} ⇒ realClaims returned UNCHANGED — the SAME reference, so the caller's
//     "no overlay" path is referentially safe (===) and allocation-free.
//   • effective = narrowHeld(bundle, realClaims, spec) — the VENDORED, corpus-locked
//     narrow step (descope_decide ds-0001…ds-0012). DOWN-ONLY by construction: a value
//     named in the spec that the operator does not hold contributes NOTHING.
//   • CLAIMS-KEY DUALITY (⚠ LOAD-BEARING): the COP carries BOTH `classification` and the
//     legacy `classification_level`. narrowHeld understands only BUNDLE axis names
//     (`classification`), so `classification_level` in a spec would be treated as an
//     unknown SET axis and set-intersected — nuking the claim to []. The seam therefore
//     STRIPS `classification_level` from every spec (it must never appear there), and
//     AFTER narrowHeld it MIRRORS the narrowed `classification` value back into
//     `classification_level` so both keys stay consistent for downstream readers (and the
//     legacy key can never retain an un-descoped clearance).
//   • NO NORMALIZATION (corpus ds-0010): held and spec values are NEVER trimmed / case-
//     bent / canonicalized before narrowHeld — a dirty held value (" secret") must pass
//     through unrecognized so the fail-closed evaluator, the single authority, denies it.
//     Cleaning it here would out-permit the evaluator (a down-only WIDEN).

import {
  narrowHeld,
  type DescopeSpec,
  type PolicyBundle,
  type SubjectClaims,
} from "../../../../packages/tdflite-classification-schema/src/index.ts";

/** Re-exported so the UI + gate lanes import the spec/claims types from the seam. */
export type { DescopeSpec, PolicyBundle, SubjectClaims };

/** The canonical bundle axis for the classification ceiling. */
const CLASS_KEY = "classification";
/** The COP's LEGACY duplicate of the classification claim. NEVER a bundle axis — it must
 *  never appear in a DescopeSpec (stripped) and is re-mirrored from `classification` after
 *  every narrow (see CLAIMS-KEY DUALITY above). */
const LEGACY_CLASS_KEY = "classification_level";

const lc = (s: string): string => String(s).toLowerCase();

/** Mirror of the vendored narrowHeld's own value projection (evaluator-identical fold):
 *  array → element-wise lowercase; non-empty scalar → singleton; absent/"" → []. Used ONLY
 *  to compare narrowHeld's OUTPUT against a spec — never to pre-clean its INPUT. */
function projectValues(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value.map(lc);
  if (value != null && value !== "") return [lc(String(value))];
  return [];
}

/** Strip the forbidden legacy key from a spec. Returns the SAME reference when nothing
 *  needed stripping (keeps the identity path allocation-free). */
function sanitizeSpec(spec: DescopeSpec): DescopeSpec {
  if (!Object.prototype.hasOwnProperty.call(spec, LEGACY_CLASS_KEY)) return spec;
  const clean: DescopeSpec = {};
  for (const [k, v] of Object.entries(spec)) {
    if (k !== LEGACY_CLASS_KEY) clean[k] = v;
  }
  return clean;
}

/** Compute the EFFECTIVE claims for a view-as step-down target.
 *
 *  • `spec` null / empty {} (including a spec that becomes empty after the legacy-key
 *    strip) ⇒ returns `realClaims` UNCHANGED — the same reference (identity).
 *  • Otherwise: a NEW claims map = narrowHeld(bundle, realClaims, spec) with the narrowed
 *    `classification` mirrored into `classification_level`. `realClaims` is NEVER mutated.
 *
 *  Down-only is inherited from the vendored primitive: omitted axes pass through, the
 *  classification ceiling only clamps down, set axes intersect with held, and unheld spec
 *  values contribute nothing. Dirty values are passed through UN-normalized for the
 *  evaluator to deny (ds-0010). */
export function descopedClaimsFor(
  realClaims: SubjectClaims,
  spec: DescopeSpec | null | undefined,
  bundle: PolicyBundle,
): SubjectClaims {
  if (spec == null) return realClaims;
  const to = sanitizeSpec(spec);
  if (Object.keys(to).length === 0) return realClaims;

  const effective = narrowHeld(bundle, realClaims, to);

  // CLAIMS-KEY DUALITY mirror: `classification` (the bundle axis narrowHeld narrowed) is
  // the source of truth; overwrite the legacy duplicate so downstream readers of EITHER
  // key see the SAME stepped-down value — and the legacy key can never leak the real
  // clearance past a classification step-down.
  if (Object.prototype.hasOwnProperty.call(effective, CLASS_KEY)) {
    const cls = effective[CLASS_KEY];
    effective[LEGACY_CLASS_KEY] = Array.isArray(cls) ? [...cls] : cls;
  }
  return effective;
}

/** EXACTNESS flag for the UI + gate: true iff narrowHeld(bundle, realClaims, spec) will
 *  reproduce the spec EXACTLY on every axis the spec names — i.e. for every axis the
 *  operator holds ⊇ the spec's requested values, so the preview really IS the named
 *  target. False ⇒ the preview is STRICTER than the named target (some requested value is
 *  not held, or the ceiling clamps below the requested level).
 *
 *  Pure, no side effects, never mutates its inputs. Comparison is per-axis SET equality
 *  under the evaluator's own case-fold (the vendored projection) against narrowHeld's
 *  actual output — so it is true exactly when the narrow step reproduces the spec, by
 *  construction. Spec values should be the canonical bundle spellings (a dirty spec value
 *  reads as not-reproduced ⇒ false). The legacy `classification_level` key is stripped
 *  from the spec here exactly as in descopedClaimsFor; a null/empty spec is trivially
 *  dominated (identity). */
export function specDominatedByHeld(
  realClaims: SubjectClaims,
  spec: DescopeSpec | null | undefined,
  bundle: PolicyBundle,
): boolean {
  if (spec == null) return true;
  const to = sanitizeSpec(spec);
  const axes = Object.keys(to);
  if (axes.length === 0) return true;

  const narrowed = narrowHeld(bundle, realClaims, to);
  for (const axis of axes) {
    const want = new Set(projectValues(to[axis]));
    const got = new Set(projectValues(narrowed[axis]));
    if (want.size !== got.size) return false;
    for (const v of want) if (!got.has(v)) return false;
  }
  return true;
}
